/*!
 * @copyright
 * Copyright(c) 2018 SICK AG
 */

import {
  CapNames,
  Director,
  Stage,
  DefaultCaps,
  DefaultNavigators,
  Materials,
  CameraDesc,
  Text2DFontLoader,
  Text2D,
  FloorKind,
  SceneCompass,
  FloorDesc,
  CapDataDesc,
  Text2DStyle,
} from '@sick-davinci/glow';
import {
  CrownViewCapData, CrownPointCloudCap, CrownTextCap, CrownShapeCap, CrownMeshCap, CrownImageCap, CrownScanCap, CrownHeightmapCap,
} from '@sick-davinci/glow-crown';
import { CrownConnectionWebSocket, CrownProtocolMsgPack, LogLevel } from '@sick-davinci/device-communication';
import { Mesh, BoxBufferGeometry, Object3D, Vector3 } from 'three';

import { DropDown, Option } from '@sick-davinci/basic-elements';

export const dependencies = [DropDown, Option];  // avoid wepack removal

/**
 * Demo:    CrownParendId
 * Description:
 *   This demo application shows how parent Ids can be used to link objects to each other via Crown.
 *   Use the included app with an AppEngine to see how the parent id is used with the CrownView interface.
 *   Without the app the user can decide to which of the 2 boxes the text is attached to.
 *   If connected the app creates several iconics which change their parent boxes alternately.
 *   Observe that when changing parents the position, rotation and scaling is adapted to the parent.
 * Features:
 *  - Orbit navigation
 *  - Capability handling
 *  - Scene compass
 *  - ViewCrown / WebSocket connection
 *  - Native threejs code
 */
export const run = (async (viewParent: HTMLElement) => {

  // ** setup the stage *
  const camDesc: CameraDesc = {
    name: 'default',
    from: [0, 20, 100],
    to: [0, 0, 0],
    up: [0, 1, 0],
    fov: 50,
  };

  const stage = new Stage(viewParent, {
    cameras: [camDesc],
    scenes: [{ name: 'default' }],
    viewports: [{
      name: 'default',
      scene: 'default',
      camera: 'default',
      navigator: 'orbit',
      bgcolor: 0x393939,
    }],
  });

  // * Add the scene compass for better orientation
  SceneCompass.create(stage, 'default', 'default');

  // * Add an Orbit Navigator
  DefaultNavigators.initOrbit(stage, 'orbit');

  // * Data handling using capabilities
  const director = new Director(stage);

  // Add all Crown caps
  director.addCapability(new CrownShapeCap());
  director.addCapability(new CrownMeshCap());
  director.addCapability(new CrownImageCap());
  director.addCapability(new CrownScanCap());
  director.addCapability(new CrownHeightmapCap());
  director.addCapability(new CrownPointCloudCap());
  director.addCapability(await createTextCap());

  DefaultCaps.init(director); // addCapability for all default caps

  // * Add some light so there is smth to see
  director.handle(CapNames.LIGHT, { lightType: 'point', color: 0xfffff0, orientation: { position: [1500, 800, -1300] } });
  director.handle(CapNames.LIGHT, { lightType: 'ambient', color: 0x888888 });

  // * Add a floor for better orientation
  director.handle(CapNames.FLOOR, {
    payload: {
      floor: FloorKind.WIREFRAME,
      size: 256,
      segments: 128,
      color: 0x555555,
    },
    orientation: {
      position: [0, -.01, 0],
    },
  } as CapDataDesc<FloorDesc>);

  const objectSel = document.getElementById('object') as DropDown;

  // * Add 2 boxes using native threejs code
  stage.applyThreeDirect((scene) => {
    const box1 = createBox(0xe0e0e0, [10, 10, 10], [-10, 5, 0]);
    box1.name = 'Box1';
    const box2 = createBox(0xe0e0e0, [10, 10, 10], [10, 5, 0]);
    box2.rotateX(-0.5);
    box2.rotateY(-0.2);
    box2.rotateZ(0.3);
    box2.scale.set(1.1, 1.5, 3);
    box2.name = 'Box2';
    scene.add(box1, box2);

    const o1 = new Option();
    o1.value = box1.name;
    o1.innerText = box1.name;
    objectSel.appendChild(o1);
    const o2 = new Option();
    o2.value = box2.name;
    o2.innerText = box2.name;
    objectSel.appendChild(o2);
    objectSel.selectItem(o1);
  });
  const font = await Text2DFontLoader.getFont('./resources/Text2D/CutiveMono-Regular.json', './resources/Text2D/');
  const style = new Text2DStyle();
  style.opacity = 1;
  font.setStyle(style);

  stage.start();  // start visualizing data

  // ** Connect to the device via the ViewCrown using the provided ip and view-id
  const cc = new CrownConnectionWebSocket('127.0.0.1', 'crownMSGPACK', 3000);
  const cp = new CrownProtocolMsgPack();
  const view = new CrownViewCapData(cc, cp, '3D');

  // ** Set all necessary callback functions for the device connection
  view.onPresentStart(() => stage.lockRenderer());
  view.onPresentEnd(() => stage.unlockRenderer());
  view.onPresentClear(() => stage.clearAllScenes());
  view.onPresentRemove((id) => stage.clearObject(stage.getObjectIdByName(id)));
  view.onPresentAddCapData((capData) => {
    director.handleCapData(capData);
  });

  // ** setup user interaction **
  // * let the user choose which box to attach the text to
  let currentObject = stage.getObjectByName(objectSel.value);
  objectSel.addEventListener('change', ((evt) => {
    const val = (evt as any).detail;
    currentObject = stage.getObjectByName(val);
    addTextToScene('child');
  }), true);
  addTextToScene('child');

  view.onConnectionChanged((connected) => {
    view.logMessage(LogLevel.INFO, 'Viewer connected.');
    objectSel.disabled = connected; // auto-mode when connected
  });

  function addTextToScene(text: string) {
    stage.applyThreeDirect((scene) => {
      const txt = createText(text);
      txt.name = 'text';
      scene.addToParent(currentObject.name, txt);
    });
  }

  function createText(text: string): Object3D {
    const textRenderer = new Text2D(font);
    textRenderer.setLineHeight(3);
    textRenderer.setLineSpacing(1);
    textRenderer.setText(text);
    textRenderer.position.add(new Vector3(-5, 6, 6));
    return textRenderer;
  }
});

function createBox(color: number, size: [number, number, number], position: [number, number, number]) {
  const box =
    new Mesh(
      new BoxBufferGeometry(...size),
      Materials.create({ type: 'normal', params: { opacity: 0.5, transparent: true } }),
    );
  box.position.set(...position);
  box.updateMatrix();
  return box;
}

// * create text cap with necessary font resources *
async function createTextCap() {
  const baseUrl = './resources/Text2D/';
  const fonts = {
    SANSSERIF: await Text2DFontLoader.getFont(baseUrl + 'OpenSans-Regular.json', baseUrl),
    SERIF: await Text2DFontLoader.getFont(baseUrl + 'NotoSerifJP-Regular.json', baseUrl),
    MONOSPACE: await Text2DFontLoader.getFont(baseUrl + 'CutiveMono-Regular.json', baseUrl),
  };
  return new CrownTextCap(fonts);

}
